import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import maplibregl from "maplibre-gl";
import { useRouting } from "@/hooks/use-routing";
import { useMapInitialization } from "@/hooks/use-map-initialization";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { useAuth } from "@/hooks/use-auth";
import { apiBaseURL } from "@/initializers/axios-client";
import { StreetDeleteTool } from "@/components/street-delete-tool";
import { StreetCompletionBrush } from "@/components/street-completion-brush";
import { StreetIncompleteBrush } from "@/components/street-incomplete-brush";
import { RouteTooltip } from "@/components/route-tooltip";
import { RouteControls } from "@/components/route-controls";
import { SuggestedRoutesPanel } from "@/components/suggested-routes-panel";
import { MapToolsControl } from "@/components/map-tools-control";
import { LayersMenu } from "@/components/layers-menu";
import { DistanceDisplay } from "@/components/distance-display";
import { useSuggestedRoutes } from "@/hooks/use-suggested-routes";
import type { SuggestionFeature } from "@/hooks/use-suggested-routes";
import { useRouteAnimation } from "@/hooks/use-route-animation";
import { routeArrowPoints } from "@/lib/route-arrows";
import { cn } from "@/lib/utils";
import {
  useListStreets,
  useListMatchedStreets,
  useListStravaRunTracks,
} from "@/api/orval";
import type { FeatureCollection as StreetFeatureCollection } from "@/types/geojson";

import type { FeatureCollection, Geometry } from "geojson";
interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
}

export const MapView = () => {
  const { workspace, workspaceId } = useCurrentWorkspace();
  const { isAdmin } = useAuth();
  const { data: streets } = useListStreets(
    { workspaceId: workspaceId ?? "" },
    // Only the admin tools need the full street collection (for lasso/brush
    // geometry math); everyone else renders base streets from vector tiles.
    { query: { enabled: !!workspaceId && isAdmin } },
  );
  const { data: matchedStreets } = useListMatchedStreets(
    { workspaceId: workspaceId ?? "" },
    { query: { enabled: !!workspaceId } },
  );
  const { data: stravaRuns } = useListStravaRunTracks(
    { workspaceId: workspaceId ?? "" },
    { query: { enabled: !!workspaceId } },
  );
  const [isDragging, setIsDragging] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [brushMode, setBrushMode] = useState(false);
  const [eraseMode, setEraseMode] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
  });

  const {
    routingMode,
    waypoints,
    routeGeometry,
    totalDistance,
    elevationGain,
    canUndo,
    canRedo,
    addWaypoint,
    undo,
    redo,
    clear,
    toggleRoutingMode,
    exportGPX,
    loadRoute,
    reverseRoute,
  } = useRouting();

  const suggestions = useSuggestedRoutes({ workspaceId });

  // Which planning mode the active routing session is in. Suggested = start/end
  // fields + generate; manual = click the map to build a route by hand. Null
  // when routing is off. The two are mutually exclusive.
  const [planMode, setPlanMode] = useState<"manual" | "suggested" | null>(null);

  // The drag handlers are bound once at map init, so reading routingMode
  // directly would capture its first-render value (false). Route through a ref
  // that tracks the latest value so dragend restores the right cursor.
  const routingModeRef = useRef(routingMode);
  routingModeRef.current = routingMode;

  const center: [number, number] =
    workspace?.longitude != null && workspace?.latitude != null
      ? [workspace.longitude, workspace.latitude]
      : [0, 0];

  // Base streets render from server-side vector tiles keyed by the workspace's
  // city (see use-map-initialization). The full-collection `streets` fetch above
  // is only for the admin tools, so it is gated to admins.
  const streetsTilesUrl = `${apiBaseURL}/workspaces/${workspaceId}/streets/tiles/{z}/{x}/{y}.pbf`;

  // Memoized so an unrelated re-render (e.g. dropping a route pin) doesn't
  // rebuild these and re-run the setData effects, which re-parse the whole
  // matched-streets layer on the map and lag the click.
  const matchedStreetsGeoJSON: FeatureCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features:
        matchedStreets?.features?.map((street) => ({
          id: street.id as number,
          type: "Feature" as const,
          geometry: street.geometry as unknown as Geometry,
          properties: {
            name: street.properties.name,
            progress_percentage: street.properties.progress_percentage,
            street_id: street.properties.street_id,
            matched: true,
          },
        })) || [],
    }),
    [matchedStreets],
  );

  const stravaRunsGeoJSON: FeatureCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features:
        stravaRuns?.features?.map((run) => ({
          id: run.id as number,
          type: "Feature" as const,
          geometry: run.geometry as unknown as Geometry,
          properties: {
            name: run.properties.name,
          },
        })) || [],
    }),
    [stravaRuns],
  );

  const handleDragStart = useCallback(() => {
    if (map.current) {
      map.current.getCanvas().style.cursor = "grab";
      setIsDragging(true);
      setTooltip((prev) => ({ ...prev, visible: false }));
    }
  }, []);

  const handleDrag = useCallback(() => {
    if (map.current) {
      map.current.getCanvas().style.cursor = "grabbing";
      setTooltip((prev) => ({ ...prev, visible: false }));
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (map.current) {
      map.current.getCanvas().style.cursor = routingModeRef.current
        ? "crosshair"
        : "grab";
      setIsDragging(false);
    }
  }, []);

  const handleToggleDeleteMode = useCallback(() => {
    if (deleteMode) {
      setDeleteMode(false);
    } else {
      // Disable other modes
      setBrushMode(false);
      setEraseMode(false);
      if (routingMode) {
        toggleRoutingMode();
      }
      setDeleteMode(true);
    }
  }, [deleteMode, routingMode, toggleRoutingMode]);

  const handleToggleBrushMode = useCallback(() => {
    if (brushMode) {
      setBrushMode(false);
    } else {
      // Disable other modes
      setDeleteMode(false);
      setEraseMode(false);
      if (routingMode) {
        toggleRoutingMode();
      }
      setBrushMode(true);
    }
  }, [brushMode, routingMode, toggleRoutingMode]);

  const handleToggleEraseMode = useCallback(() => {
    if (eraseMode) {
      setEraseMode(false);
    } else {
      // Disable other modes
      setDeleteMode(false);
      setBrushMode(false);
      if (routingMode) {
        toggleRoutingMode();
      }
      setEraseMode(true);
    }
  }, [eraseMode, routingMode, toggleRoutingMode]);

  // Enter (or, if already active, cancel) the suggested-routes mode. Entering it
  // clears any manual route and the other curation tools first.
  const enterSuggested = useCallback(() => {
    if (planMode === "suggested") {
      if (routingMode) toggleRoutingMode();
      return;
    }
    setDeleteMode(false);
    setBrushMode(false);
    setEraseMode(false);
    clear();
    if (!routingMode) toggleRoutingMode();
    setPlanMode("suggested");
  }, [planMode, routingMode, toggleRoutingMode, clear]);

  // Enter (or cancel) the manual route builder. Entering it clears any suggested
  // start/end/candidates first.
  const enterManual = useCallback(() => {
    if (planMode === "manual") {
      if (routingMode) toggleRoutingMode();
      return;
    }
    setDeleteMode(false);
    setBrushMode(false);
    setEraseMode(false);
    suggestions.reset();
    if (!routingMode) toggleRoutingMode();
    setPlanMode("manual");
  }, [planMode, routingMode, toggleRoutingMode, suggestions.reset]);

  const { mapContainer, map, layersSlot, toolsSlot } = useMapInitialization({
    center,
    streetsTilesUrl,
    matchedStreetsGeoJSON,
    stravaRunsGeoJSON,
    onDragStart: handleDragStart,
    onDrag: handleDrag,
    onDragEnd: handleDragEnd,
    onLoad: () => setMapLoaded(true),
  });

  // The bottom-left tools control (portaled into toolsSlot) is shown only for
  // admins outside routing mode; toggle the native slot so an empty group box
  // never shows.
  useEffect(() => {
    if (toolsSlot) {
      toolsSlot.style.display = isAdmin && !routingMode ? "" : "none";
    }
  }, [toolsSlot, isAdmin, routingMode]);

  const routeAnimation = useRouteAnimation({ map });

  const handleMapClick = useCallback(
    async (event: maplibregl.MapMouseEvent) => {
      if (!routingMode) return;

      const coordinates: [number, number] = [
        event.lngLat.lng,
        event.lngLat.lat,
      ];

      // Suggested mode: a click only sets the start/end pin while picking one on
      // the map; it never adds a manual waypoint.
      if (planMode === "suggested") {
        if (suggestions.pickingStart) {
          suggestions.setStartPoint(coordinates);
          suggestions.setStartLabel("Dropped pin");
          suggestions.setPickingStart(false);
        } else if (suggestions.pickingEnd) {
          suggestions.setEndPoint(coordinates);
          suggestions.setEndLabel("Dropped pin");
          suggestions.setPickingEnd(false);
        }
        return;
      }

      // Manual mode: every click extends the hand-built route (first = start).
      await addWaypoint(coordinates);
    },
    [
      addWaypoint,
      routingMode,
      planMode,
      suggestions.pickingStart,
      suggestions.setStartPoint,
      suggestions.setStartLabel,
      suggestions.setPickingStart,
      suggestions.pickingEnd,
      suggestions.setEndPoint,
      suggestions.setEndLabel,
      suggestions.setPickingEnd,
    ],
  );

  const handleGenerateSuggestions = useCallback(() => {
    const c = map.current?.getCenter();
    const fallback: [number, number] = c ? [c.lng, c.lat] : center;
    suggestions.generate(fallback);
  }, [suggestions.generate, center]);

  // Picking a candidate reveals it by growing the orange `route` line itself.
  // The geometry is committed to routing state (which drives the Download /
  // Reverse controls and the distance bar) only once the reveal lands, so the
  // full route never flashes onto the map before it draws.
  const handleSelectCandidate = useCallback(
    (feature: SuggestionFeature) => {
      suggestions.setSelectedId(feature.id);
      routeAnimation.play(feature.geometry.coordinates, () =>
        loadRoute(
          {
            type: "Feature",
            properties: feature.properties,
            geometry: feature.geometry,
          },
          feature.properties.distance_m,
        ),
      );
    },
    [loadRoute, suggestions.setSelectedId, routeAnimation.play],
  );

  // Move the map to a point and pin it as start or end with a human label.
  const placePoint = useCallback(
    (which: "start" | "end", coord: [number, number], label: string) => {
      if (which === "start") {
        suggestions.setStartPoint(coord);
        suggestions.setStartLabel(label);
        suggestions.setPickingStart(false);
      } else {
        suggestions.setEndPoint(coord);
        suggestions.setEndLabel(label);
        suggestions.setPickingEnd(false);
      }
      map.current?.flyTo({ center: coord, zoom: 14 });
    },
    [suggestions],
  );

  const useCurrentLocation = useCallback(
    (which: "start" | "end") => {
      navigator.geolocation?.getCurrentPosition(
        (pos) =>
          placePoint(
            which,
            [pos.coords.longitude, pos.coords.latitude],
            "Current location",
          ),
        (err) => console.warn("Geolocation unavailable:", err.message),
        { enableHighAccuracy: true },
      );
    },
    [placePoint],
  );

  const startField = {
    label: suggestions.startLabel,
    picking: suggestions.pickingStart,
    onSelectPlace: (coord: [number, number], label: string) =>
      placePoint("start", coord, label),
    onChooseOnMap: () => {
      suggestions.setPickingEnd(false);
      suggestions.setPickingStart(true);
    },
    onUseCurrentLocation: () => useCurrentLocation("start"),
    onClear: () => {
      suggestions.setStartPoint(null);
      suggestions.setStartLabel(null);
      suggestions.setPickingStart(false);
    },
  };

  const endField = {
    label: suggestions.endLabel,
    picking: suggestions.pickingEnd,
    onSelectPlace: (coord: [number, number], label: string) =>
      placePoint("end", coord, label),
    onChooseOnMap: () => {
      suggestions.setPickingStart(false);
      suggestions.setPickingEnd(true);
    },
    onUseCurrentLocation: () => useCurrentLocation("end"),
    onClear: () => {
      suggestions.setEndPoint(null);
      suggestions.setEndLabel(null);
      suggestions.setPickingEnd(false);
    },
  };

  // Clear suggestions and stop any running animation when routing mode turns off.
  // We wipe the map sources directly (not just React state) so nothing lingers if
  // a state-driven redraw is skipped, e.g. across hot reloads.
  useEffect(() => {
    if (routingMode) return;
    setPlanMode(null);
    suggestions.reset();
    routeAnimation.stop();

    // Wipe every routing/suggestion source directly. Guard with try/catch (not
    // isStyleLoaded, which is false whenever tiles are mid-load and would skip
    // the cleanup) so nothing lingers after closing.
    const m = map.current;
    if (!m) return;
    const emptyFC = { type: "FeatureCollection" as const, features: [] };
    const emptyLine = {
      type: "Feature" as const,
      properties: {},
      geometry: { type: "LineString" as const, coordinates: [] },
    };
    try {
      for (const id of [
        "suggested-routes",
        "start-point",
        "end-point",
        "route-animation-point",
        "route-arrows",
      ]) {
        (m.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(
          emptyFC,
        );
      }
      (m.getSource("route") as maplibregl.GeoJSONSource | undefined)?.setData(
        emptyLine,
      );
    } catch {
      // map not created yet or torn down.
    }
  }, [routingMode, suggestions.reset, routeAnimation.stop, mapLoaded]);

  // Only animate when switching to a different city. On first open the map is
  // already initialized at the city center (see `center` above), so flying would
  // just be a jarring no-op from the same spot.
  const lastFlownWorkspaceId = useRef<string | undefined>(workspace?.id);
  useEffect(() => {
    if (
      !map.current ||
      workspace?.longitude == null ||
      workspace?.latitude == null
    )
      return;
    if (workspace.id === lastFlownWorkspaceId.current) return;
    lastFlownWorkspaceId.current = workspace.id;

    map.current.flyTo({
      center: [workspace.longitude, workspace.latitude],
      zoom: 12,
      duration: 4000,
      essential: true,
    });
  }, [workspace?.id]);

  useEffect(() => {
    if (!map.current) return;

    const canvas = map.current.getCanvas();
    if (routingMode) {
      canvas.style.cursor = "crosshair";
    } else {
      canvas.style.cursor = "grab";
    }
  }, [routingMode]);

  useEffect(() => {
    if (!map.current) return;

    const mouseMoveHandler = (e: maplibregl.MapMouseEvent) => {
      if (
        !waypoints?.length &&
        routingMode &&
        planMode === "manual" &&
        !isDragging
      ) {
        setTooltip({
          visible: true,
          x: e.point.x,
          y: e.point.y + 25,
        });
      } else {
        setTooltip((prev) => ({ ...prev, visible: false }));
      }
    };

    const mouseOutHandler = () => {
      setTooltip((prev) => ({ ...prev, visible: false }));
    };

    map.current.on("mousemove", mouseMoveHandler);
    map.current.on("mouseout", mouseOutHandler);

    return () => {
      if (map.current) {
        map.current.off("mousemove", mouseMoveHandler);
        map.current.off("mouseout", mouseOutHandler);
      }
    };
  }, [waypoints?.length, routingMode, planMode, isDragging]);

  useEffect(() => {
    if (!map.current || !handleMapClick) return;

    map.current.on("click", handleMapClick);

    return () => {
      map.current?.off("click", handleMapClick);
    };
  }, [handleMapClick]);

  useEffect(() => {
    if (!map.current) return;

    const source = map.current.getSource("route") as maplibregl.GeoJSONSource;
    if (source) {
      if (routeGeometry) {
        source.setData(routeGeometry);
      } else {
        source.setData({
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: [],
          },
        });
      }
    }
  }, [routeGeometry]);

  // Direction arrows anchored to fixed points along the route (not
  // line-symbol placement) so they keep their spots across zooms. Recomputed
  // whenever the geometry changes, which includes Reverse flipping the array.
  useEffect(() => {
    if (!map.current) return;
    const source = map.current.getSource(
      "route-arrows",
    ) as maplibregl.GeoJSONSource;
    if (!source) return;
    const coords = routeGeometry?.geometry?.coordinates as
      | [number, number][]
      | undefined;
    source.setData(routeArrowPoints(coords));
  }, [routeGeometry, mapLoaded]);

  // Draw the suggested-route candidates (violet). The selected one is drawn as
  // the orange `route` instead, so it is filtered out of this overlay.
  useEffect(() => {
    if (!map.current) return;
    const source = map.current.getSource(
      "suggested-routes",
    ) as maplibregl.GeoJSONSource;
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: suggestions.candidates
        .filter((c) => c.id !== suggestions.selectedId)
        .map((c) => ({
          type: "Feature",
          id: c.id,
          geometry: c.geometry,
          properties: c.properties,
        })),
    });
  }, [suggestions.candidates, suggestions.selectedId, mapLoaded]);

  // Draw the start pin.
  useEffect(() => {
    if (!map.current) return;
    const source = map.current.getSource(
      "start-point",
    ) as maplibregl.GeoJSONSource;
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: suggestions.startPoint
        ? [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: suggestions.startPoint,
              },
              properties: {},
            },
          ]
        : [],
    });
  }, [suggestions.startPoint, mapLoaded]);

  // Draw the end pin.
  useEffect(() => {
    if (!map.current) return;
    const source = map.current.getSource(
      "end-point",
    ) as maplibregl.GeoJSONSource;
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: suggestions.endPoint
        ? [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: suggestions.endPoint,
              },
              properties: {},
            },
          ]
        : [],
    });
  }, [suggestions.endPoint, mapLoaded]);

  useEffect(() => {
    if (!map.current || !waypoints) return;

    const waypointsSource = map.current.getSource(
      "waypoints",
    ) as maplibregl.GeoJSONSource;
    if (waypointsSource) {
      waypointsSource.setData({
        type: "FeatureCollection",
        features: waypoints.map((wp, index) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: wp.coordinates,
          },
          properties: {
            label: wp.label,
            pointType:
              index === 0
                ? "start"
                : index === waypoints.length - 1
                  ? "end"
                  : "waypoint",
          },
        })),
      });
    }
  }, [waypoints]);

  useEffect(() => {
    if (!map.current || !matchedStreets) return;

    const source = map.current.getSource(
      "matched-streets",
    ) as maplibregl.GeoJSONSource;
    if (source) {
      source.setData(matchedStreetsGeoJSON);
    }
  }, [matchedStreets, matchedStreetsGeoJSON]);

  useEffect(() => {
    if (!map.current || !stravaRuns) return;

    const source = map.current.getSource(
      "strava-runs",
    ) as maplibregl.GeoJSONSource;
    if (source) {
      source.setData(stravaRunsGeoJSON);
    }
  }, [stravaRuns, stravaRunsGeoJSON]);

  return (
    // Fill the parent (below the page header), not the full viewport, so the
    // bottom-left map controls aren't pushed off-screen. `map-routing` lifts
    // those controls above the distance bar while a route is being drawn.
    <div className={cn("relative h-full w-full", routingMode && "map-routing")}>
      <div ref={mapContainer} className="h-full w-full" />

      {layersSlot &&
        createPortal(
          <LayersMenu map={mapLoaded ? map.current : null} />,
          layersSlot,
        )}

      <RouteControls
        planMode={planMode}
        canUndo={canUndo}
        canRedo={canRedo}
        hasWaypoints={waypoints.length > 0}
        hasRoute={!!routeGeometry}
        onSelectSuggested={enterSuggested}
        onSelectManual={enterManual}
        onUndo={undo}
        onRedo={redo}
        onClear={clear}
        onReverse={reverseRoute}
        onExportGPX={exportGPX}
      />

      <SuggestedRoutesPanel
        visible={routingMode && planMode === "suggested"}
        distancePresets={suggestions.distancePresets}
        distanceKm={suggestions.distanceKm}
        onDistanceChange={suggestions.setDistanceKm}
        loop={suggestions.loop}
        onLoopChange={suggestions.setLoop}
        hasEnd={!!suggestions.endPoint}
        searchBias={center}
        startField={startField}
        endField={endField}
        candidates={suggestions.candidates}
        selectedId={suggestions.selectedId}
        onSelect={handleSelectCandidate}
        onGenerate={handleGenerateSuggestions}
        hasRoute={!!routeGeometry}
        onReverse={reverseRoute}
        onExportGPX={exportGPX}
        isGenerating={suggestions.isGenerating}
        totalNetworkKm={workspace?.progress?.total_network_km ?? undefined}
      />

      <RouteTooltip visible={tooltip.visible} x={tooltip.x} y={tooltip.y} />

      <DistanceDisplay
        totalDistance={totalDistance}
        elevationGain={elevationGain}
        visible={routingMode}
      />

      {/* Street curation tools: admins only. The trigger buttons live in the
          bottom-left tools control (portaled below the layers button); these
          components render the active-mode panels and brush logic. */}
      {isAdmin && (
        <>
          {toolsSlot &&
            createPortal(
              <MapToolsControl
                brushActive={brushMode}
                onToggleBrush={handleToggleBrushMode}
                eraseActive={eraseMode}
                onToggleErase={handleToggleEraseMode}
                deleteActive={deleteMode}
                onToggleDelete={handleToggleDeleteMode}
              />,
              toolsSlot,
            )}

          <StreetDeleteTool
            map={mapLoaded ? map.current : null}
            streets={streets as unknown as StreetFeatureCollection}
            matchedStreets={
              matchedStreetsGeoJSON as unknown as StreetFeatureCollection
            }
            active={deleteMode}
            onToggle={handleToggleDeleteMode}
            hideTrigger
          />

          <StreetCompletionBrush
            map={mapLoaded ? map.current : null}
            streets={streets as unknown as StreetFeatureCollection}
            active={brushMode}
            onToggle={handleToggleBrushMode}
            hideTrigger
          />

          <StreetIncompleteBrush
            map={mapLoaded ? map.current : null}
            streets={streets as unknown as StreetFeatureCollection}
            active={eraseMode}
            onToggle={handleToggleEraseMode}
            hideTrigger
          />
        </>
      )}
    </div>
  );
};
